import { type ReactNode } from 'react'
import { Typography } from 'shared/ui'

export const useCaseDescMinTextLength = 40
export const useCaseDescMaxTextLength = 500
export const sampleMessageMinTextLength = 20
export const sampleMessageMaxTextLength = 500

export const useCaseDescExampleMock = `This campaign sends appointment information - confirmation
   & reminder messages, as well as promotional material for special offers to our
   customers once they have booked an appointment with us on our website and opted-
   in to receive promotional and notification SMS from us.`

export const useSampleMessageExampleMock = `SALESMSG, LLC: Don't miss out! For a limited time, you can
   enjoy an additional 15% off your entire order. Simply use the promo code 15OFF at checkout and
   watch the savings add up. Reply STOP to end.`

export const USE_CASE_DESCRIPTION: Record<number, { label: string; content: ReactNode }> = {
  1: {
    label: 'Account Notifications',
    content: (
      <>
        Example sends SMS messages to users who opt in via web form during account registration.
        Messages may include account security alerts, password reset confirmations, and billing
        statement notifications. Message frequency may vary. Message and data rates may apply. Reply
        STOP to opt out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  2: {
    label: 'Customer Care',
    content: (
      <>
        Example sends SMS messages to users who opt in via online booking system. Messages may
        include appointment reminders, service follow-ups, and support resolution updates. Message
        frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for
        assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  3: {
    label: 'Higher Education',
    content: (
      <>
        Example sends SMS messages to users who opt in via admissions enrollment form. Messages may
        include class schedule changes, tuition payment reminders, and campus safety alerts. Message
        frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for
        assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  6: {
    label: 'Events',
    content: (
      <>
        Example sends SMS messages to users who opt in via event registration portal. Messages may
        include registration confirmations, schedule reminders, and last-minute event updates.
        Message frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP
        for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  8: {
    label: 'Marketing',
    content: (
      <>
        Example sends SMS messages to users who opt in via website sign-up form. Messages may
        include promotional offers, seasonal discounts, and product launch announcements. Message
        frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for
        assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  42: {
    label: 'Charity',
    content: (
      <>
        Example sends SMS messages to users who opt in via website donation form. Messages may
        include donation campaign updates, fundraising event invitations, and volunteer coordination
        requests. Message frequency may vary. Message and data rates may apply. Reply STOP to opt
        out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  43: {
    label: 'Delivery Notifications',
    content: (
      <>
        Example sends SMS messages to users who opt in via checkout page. Messages may include
        shipping status updates, estimated delivery times, and delivery confirmation alerts. Message
        frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for
        assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  44: {
    label: 'Fraud Alert Messaging',
    content: (
      <>
        Example sends SMS messages to users who opt in via security notification enrollment.
        Messages may include suspicious activity alerts, unauthorized transaction warnings, and
        account compromise notifications. Message frequency may vary. Message and data rates may
        apply. Reply STOP to opt out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  45: {
    label: 'K-12',
    content: (
      <>
        Example sends SMS messages to users who opt in via student enrollment form. Messages may
        include attendance notifications, school closure alerts, and parent-teacher conference
        reminders. Message frequency may vary. Message and data rates may apply. Reply STOP to opt
        out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  46: {
    label: 'Polling and Voting (Non-Political)',
    content: (
      <>
        Example sends SMS messages to users who opt in via research participant registration.
        Messages may include survey invitations, feedback requests, and poll participation
        reminders. Message frequency may vary. Message and data rates may apply. Reply STOP to opt
        out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  47: {
    label: 'Political (Election Campaigns)',
    content: (
      <>
        Example sends SMS messages to users who opt in via campaign website sign-up. Messages may
        include election reminders, rally invitations, and voter registration updates. Message
        frequency may vary. Message and data rates may apply. Reply STOP to opt out or HELP for
        assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  48: {
    label: 'Public Service Announcement',
    content: (
      <>
        Example sends SMS messages to users who opt in via emergency alert enrollment system.
        Messages may include public health advisories, severe weather warnings, and community safety
        notifications. Message frequency may vary. Message and data rates may apply. Reply STOP to
        opt out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  49: {
    label: 'Security Alert',
    content: (
      <>
        Example sends SMS messages to users who opt in via account security settings. Messages may
        include login attempt notifications, password change confirmations, and unrecognized device
        alerts. Message frequency may vary. Message and data rates may apply. Reply STOP to opt out
        or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
  50: {
    label: '2FA',
    content: (
      <>
        Example sends SMS messages to users who opt in via two-factor authentication setup. Messages
        may include one-time verification codes for login and sensitive account action
        confirmations. Message frequency may vary. Message and data rates may apply. Reply STOP to
        opt out or HELP for assistance. Consent is not a condition of purchase.
        <br />
        Privacy Policy:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/privacy
        </Typography>
        .
        <br />
        Terms & Conditions:{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/terms
        </Typography>
        .
      </>
    ),
  },
}

export const USE_CASE_SAMPLE_MESSAGE: Record<number, { label: string; content: ReactNode }> = {
  1: {
    label: 'Account Notifications',
    content: (
      <>
        Hi John, this is Example. Your account password was recently updated. If you did not request
        this change, please contact support immediately. Reply STOP to opt out or HELP for
        assistance.
      </>
    ),
  },
  2: {
    label: 'Customer Care',
    content: (
      <>
        Hi John, this is Example. Your appointment is confirmed for tomorrow at 2:00 PM. Reply
        CONFIRM to keep it or call us to reschedule. Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  3: {
    label: 'Higher Education',
    content: (
      <>
        Hi John, this is Example. The deadline to register for Fall 2026 courses is May 15. Log in
        to your student portal to select your classes. Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  6: {
    label: 'Events',
    content: (
      <>
        Hi John, this is Example. You&#39;re confirmed for our Annual Business Summit on April 10 at
        9 AM. View the full agenda at{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/summit
        </Typography>
        . Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  8: {
    label: 'Marketing',
    content: (
      <>
        Hi John, this is Example. Flash sale this weekend only - get 20% off sitewide with code
        SAVE20 at checkout. Shop now at{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/shop
        </Typography>
        . Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  42: {
    label: 'Charity',
    content: (
      <>
        Hi John, this is Example. Our spring fundraiser is live - your donation helps us reach our
        goal and support those in need. Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  43: {
    label: 'Delivery Notifications',
    content: (
      <>
        Hi John, this is Example. Your order #100234 has shipped and is expected to arrive by
        Friday. Track your package at{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/track
        </Typography>
        . Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  44: {
    label: 'Fraud Alert Messaging',
    content: (
      <>
        Hi John, this is Example. A transaction of $247.00 was attempted on your account ending in
        4521. If this was not you, call us at (555) 987-6543. Reply STOP to opt out or HELP for
        assistance.
      </>
    ),
  },
  45: {
    label: 'K-12',
    content: (
      <>
        Hey, this is Example. Your child Alex was marked absent today. If this is an error, please
        contact the front office at (555) 222-3344. Reply STOP to end.
      </>
    ),
  },
  46: {
    label: 'Polling and Voting (Non-Political)',
    content: (
      <>
        Hi John, this is Example. We&#39;d love your feedback! Take our 2-minute customer
        satisfaction survey at{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/survey
        </Typography>
        . Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  47: {
    label: 'Political (Election Campaigns)',
    content: (
      <>
        Hi John, this is Example. Election Day is November 3. Find your nearest polling station and
        verify your voter registration at{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/vote
        </Typography>
        . Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  48: {
    label: 'Public Service Announcement',
    content: (
      <>
        Hi John, this is Example. A severe thunderstorm warning is in effect for your area until 8
        PM. Avoid unnecessary travel and stay indoors. Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  49: {
    label: 'Security Alert',
    content: (
      <>
        Hi John, this is Example. A new login was detected from an unrecognized device. If this was
        not you, secure your account at{' '}
        <Typography ariaLabel={'linkSample'} link={true}>
          example.com/security
        </Typography>
        . Reply STOP to opt out or HELP for assistance.
      </>
    ),
  },
  50: {
    label: '2FA',
    content: (
      <>
        Hi John, this is Example. Your verification code is 847291. Enter it to complete your login.
        This code expires in 10 minutes. Do not share it with anyone. Reply STOP to opt out or HELP
        for assistance.
      </>
    ),
  },
}
